import { useEffect } from "react";
import { Coffee, Github } from "lucide-react";
import brandIconDark from "./assets/brand/midimaster-dark.png";
import { InteractiveDemo } from "./components/InteractiveDemo";
import { demoScreenshotSources } from "./demoData";

const downloadUrl = "/download";
const sourceUrl = "/github";
const discordUrl = "/discord";
const supportUrl = "https://buymeacoffee.com/midimaster";
const preloadedImages = new Map<string, HTMLImageElement>();

function preloadImage(source: string) {
  if (preloadedImages.has(source)) {
    return;
  }

  const image = new Image();
  image.decoding = "async";
  image.src = source;
  preloadedImages.set(source, image);
}

function WindowsIcon() {
  return (
    <svg className="button-icon button-icon-filled" viewBox="0 0 24 24" aria-hidden="true">
      <path d="M3 5.3 10.8 4v7.4H3V5.3Zm9.2-1.5L21 2.5v8.9h-8.8V3.8ZM3 12.6h7.8V20L3 18.8v-6.2Zm9.2 0H21v8.9l-8.8-1.3v-7.6Z" />
    </svg>
  );
}

function DiscordIcon() {
  return (
    <svg className="button-icon button-icon-filled" viewBox="0 0 24 24" aria-hidden="true">
      <path d="M19.7 5.4A16.8 16.8 0 0 0 15.6 4l-.2.4c1.4.4 2.1 1 2.1 1s-1.7-.9-5.4-.9-5.4.9-5.4.9.7-.6 2.1-1L8.5 4a16.8 16.8 0 0 0-4.1 1.4C1.8 9.1 1.1 12.8 1.4 16.4c1.7 1.3 3.4 2 5 2.5l1.1-1.8c-.6-.2-1.1-.5-1.6-.8l.4-.3c3.1 1.5 6.5 1.5 9.6 0l.4.3c-.5.3-1 .6-1.6.8l1.1 1.8c1.6-.5 3.3-1.2 5-2.5.4-4.2-.6-7.8-3.1-11ZM8.5 14.1c-1 0-1.8-.9-1.8-2s.8-2 1.8-2 1.8.9 1.8 2-.8 2-1.8 2Zm7 0c-1 0-1.8-.9-1.8-2s.8-2 1.8-2 1.8.9 1.8 2-.8 2-1.8 2Z" />
    </svg>
  );
}

const useCases = [
  {
    title: "Control app audio",
    description: "Adjust system and app volume with physical faders.",
  },
  {
    title: "Trigger actions",
    description: "Run media controls, hotkeys, capture actions, and app launches from buttons.",
  },
  {
    title: "Manage devices",
    description: "Map controls to playback and recording devices.",
  },
  {
    title: "Extend with plugins",
    description: "Add new app and device targets through plugins.",
  },
];

export function App() {
  useEffect(() => {
    [brandIconDark, ...demoScreenshotSources].forEach(preloadImage);
  }, []);

  return (
    <div className="site-shell">
      <header className="site-nav">
        <div className="brand">
          <img className="brand-icon" src={brandIconDark} alt="" aria-hidden="true" />
          <span>MIDIMaster</span>
        </div>
        <nav aria-label="Primary navigation">
          <a href={downloadUrl}>Download</a>
          <a href={sourceUrl}>GitHub</a>
        </nav>
      </header>

      <main id="top">
        <section className="showcase" id="demo" aria-labelledby="hero-title">
          <div className="hero-copy">
            <h1 id="hero-title">Free Windows MIDI controller mapping software.</h1>
            <p className="hero-summary">
              Turn any MIDI controller into a Windows control surface for audio apps, hotkeys, media
              controls, devices, and plugin targets.
            </p>
            <div className="hero-actions" aria-label="Primary actions">
              <a className="button button-primary" href={downloadUrl}>
                <WindowsIcon />
                Download for Windows
              </a>
              <a className="button" href={sourceUrl}>
                <Github className="button-icon" aria-hidden="true" />
                View on GitHub
              </a>
              <a className="button" href={discordUrl} target="_blank" rel="noreferrer">
                <DiscordIcon />
                Join Discord
              </a>
              <a className="button button-support" href={supportUrl} target="_blank" rel="noreferrer">
                <Coffee className="button-icon" aria-hidden="true" />
                Buy Me a Coffee
              </a>
            </div>
          </div>

          <InteractiveDemo />

          <section className="use-case-strip" aria-label="MIDIMaster use cases">
            {useCases.map((item) => (
              <article className="use-case" key={item.title}>
                <h2>{item.title}</h2>
                <p>{item.description}</p>
              </article>
            ))}
          </section>

        </section>
      </main>

      <footer className="site-footer">
        <span>
          MIDIMaster is MIT licensed. Published by MITCHELL SOFTWARE SOLUTIONS LLC. Legal contact: {" "}
          <a href="mailto:legal@midimaster.app">legal@midimaster.app</a>.
        </span>
        <span>
          <a href={supportUrl} target="_blank" rel="noreferrer">
            Support MIDIMaster
          </a>{" "}
          - Not affiliated with third-party vendors or trademarks.
        </span>
      </footer>
    </div>
  );
}
